/**
 * Centralized Date/Time Configuration for Frontend
 *
 * Mirrors backend configuration. Keep in sync with backend/src/config/dateTimeConfig.ts
 *
 * All date/time constants for the application.
 * Always use UTC to avoid timezone complexity and DST issues.
 *
 * @module dateTimeConfig
 */

export const DATE_TIME_CONFIG = {
  /**
   * Application timezone - ALWAYS UTC
   * Never use local timezones to avoid DST issues and regional complexity
   */
  TIMEZONE: 'UTC' as const,

  /**
   * Game reset time (when new daily stock becomes available)
   * Set to 2:00 AM UTC
   *
   * This is when the game date rolls over and a new daily stock is selected.
   * Users will see the new stock starting at this time each day.
   */
  GAME_RESET: {
    HOUR: 2,     // 2:00 AM UTC
    MINUTE: 0,
  },
} as const;

/**
 * Derived constants (read-only, calculated from primary config)
 */
export const DERIVED_CONSTANTS = {
  /**
   * Human-readable description of game reset time
   */
  RESET_TIME_DESCRIPTION: `New stocks available daily at ${DATE_TIME_CONFIG.GAME_RESET.HOUR.toString().padStart(2, '0')}:${DATE_TIME_CONFIG.GAME_RESET.MINUTE.toString().padStart(2, '0')} UTC`,
} as const;

// Type exports for TypeScript autocomplete
export type DateTimeConfig = typeof DATE_TIME_CONFIG;
export type DerivedConstants = typeof DERIVED_CONSTANTS;

/**
 * Helper function to check if daily reset time has passed
 * Used to determine the current game date
 */
export const isDailyResetTime = (): boolean => {
  const now = new Date();
  const utcHour = now.getUTCHours();
  const utcMinute = now.getUTCMinutes();
  const resetHour = DATE_TIME_CONFIG.GAME_RESET.HOUR;
  const resetMinute = DATE_TIME_CONFIG.GAME_RESET.MINUTE;

  // Check if it's past the configured reset time
  return utcHour > resetHour || (utcHour === resetHour && utcMinute >= resetMinute);
};

/**
 * Helper function to get the current game timestamp for comparison
 * Returns the game date (YYYY-MM-DD) based on the configured reset time
 * If it's before reset time, returns yesterday's date
 */
export const getCurrentGameTimestamp = (): string => {
  const now = new Date();

  // If it's before the configured reset time, use yesterday's date
  if (!isDailyResetTime()) {
    // Calculate yesterday in UTC (not local timezone)
    const yesterday = new Date(now);
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);
    return yesterday.toISOString().split('T')[0];
  }

  // Get today's date in UTC
  return now.toISOString().split('T')[0];
};
